import type { IWidgetConfig } from "../shared/session/interfaces/session";
import type { ClaimBlockReason } from "./bindErrors";

/** Operation waiting for the recipient address form, if the claim was held. */
export const pendingOfacOperationId = (
  config?: IWidgetConfig
): string | null => {
  if (config?.actionRequired?.type !== "OFAC_DISAMBIGUATION") return null;
  return config.operationId ?? null;
};

/** Confirmed recipient list match parked for an operator, before any charge. */
export const isRecipientListReview = (config?: IWidgetConfig): boolean =>
  config?.claimFlowState === "identity_review" &&
  config.statusReason === "RECIPIENT_LIST_HIT";
const CLAIMABLE_BINDING_STATES = new Set([
  "payment_bound",
  "awaiting_claim",
]);

/** True when the claim link's deferred payout is still open to claim. */
export const isClaimable = (config?: IWidgetConfig): boolean => {
  if (!config?.originationId) return true;

  if (
    config.bindingState &&
    !CLAIMABLE_BINDING_STATES.has(config.bindingState)
  ) {
    return false;
  }

  // Live sender CIP from claim/session. Absent = older backend; bind still enforces.
  if (config.senderCipStatus && config.senderCipStatus !== "verified") {
    return false;
  }

  return !config.bindingState || CLAIMABLE_BINDING_STATES.has(config.bindingState);
};

/** Maps a deferred binding state to the ClaimBlockedPage reason. */
export const claimBlockReasonFromBindingState = (
  bindingState?: string
): ClaimBlockReason => {
  switch (bindingState) {
    case "expired":
      return "expired";
    case "cancelled":
      return "cancelled";
    case "ofac_rejected":
      return "ofac";
    case "payout_bound":
      return "already_claimed";
    default:
      return "already_claimed";
  }
};

/**
 * Prefer terminal binding states over sender CIP waiting, so an expired link
 * does not show "wait for sender" after the transfer is already closed.
 */
export const claimBlockReasonFromConfig = (
  config?: IWidgetConfig
): ClaimBlockReason => {
  if (isRecipientListReview(config)) return "recipient_review";

  if (
    config?.bindingState &&
    !CLAIMABLE_BINDING_STATES.has(config.bindingState)
  ) {
    return claimBlockReasonFromBindingState(config.bindingState);
  }

  if (config?.senderCipStatus === "failed") {
    return "sender_failed";
  }

  if (config?.senderCipStatus && config.senderCipStatus !== "verified") {
    return "sender_waiting";
  }

  return claimBlockReasonFromBindingState(config?.bindingState);
};
